/**
 * Utility functions for formatting calendar dates and wall-clock times in Family views.
 * Anchored in UTC to avoid local timezone shifts when presenting date/time values.
 */

export function parseWallClockTime(timeStr: string): { hours: number; minutes: number } | null {
  if (!timeStr) return null
  const trimmed = timeStr.trim()
  const match = trimmed.match(/^(\d{1,2}):(\d{2})(?:\s*([ap]\.?m\.?))?$/i)
  if (!match) return null
  let hours = parseInt(match[1], 10)
  const minutes = parseInt(match[2], 10)
  const meridiem = match[3]?.toLowerCase().replace(/\./g, '')
  if (isNaN(hours) || isNaN(minutes) || minutes < 0 || minutes > 59) return null
  if (meridiem) {
    if (meridiem === 'pm' && hours < 12) hours += 12
    if (meridiem === 'am' && hours === 12) hours = 0
  }
  if (hours < 0 || hours > 23) return null
  return { hours, minutes }
}

export function formatCalendarDate(dateStr: string, locale?: string): string {
  if (!dateStr) return ''
  const parts = dateStr.split('-')
  if (parts.length !== 3) return dateStr
  const year = parseInt(parts[0], 10)
  const month = parseInt(parts[1], 10)
  const day = parseInt(parts[2], 10)
  if (isNaN(year) || isNaN(month) || isNaN(day)) return dateStr

  const utcDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0))
  return new Intl.DateTimeFormat(locale || 'en', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(utcDate)
}

export function formatWallClockTime(timeStr: string, locale?: string): string {
  if (!timeStr) return ''
  const parsed = parseWallClockTime(timeStr)
  if (!parsed) return timeStr

  const utcDate = new Date(Date.UTC(2000, 0, 1, parsed.hours, parsed.minutes, 0))
  return new Intl.DateTimeFormat(locale || 'en', {
    timeZone: 'UTC',
    hour: 'numeric',
    minute: '2-digit',
  }).format(utcDate)
}

export function formatInboxTime(ms: number, locale?: string): string {
  if (!ms || isNaN(ms)) return ''
  const date = new Date(ms)
  return new Intl.DateTimeFormat(locale || undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

/**
 * Formats an unknown date value (ISO string, Timestamp, Date, or millis) into a localized date string.
 * Used by FamilyProgressTab, EduLearnerPage, and other portfolio/activity views.
 */
export function formatEduDate(dateVal: unknown, locale?: string, timeZone?: string): string {
  if (!dateVal) return ''
  let d: Date
  if (dateVal instanceof Date) {
    d = dateVal
  } else if (
    typeof dateVal === 'object' &&
    dateVal !== null &&
    'toDate' in dateVal &&
    typeof (dateVal as { toDate: () => unknown }).toDate === 'function'
  ) {
    d = (dateVal as { toDate: () => Date }).toDate()
  } else if (
    typeof dateVal === 'object' &&
    dateVal !== null &&
    typeof (dateVal as { _seconds?: unknown })._seconds === 'number'
  ) {
    const s = (dateVal as { _seconds: number; _nanoseconds?: number })._seconds
    const ns = (dateVal as { _seconds: number; _nanoseconds?: number })._nanoseconds || 0
    d = new Date(s * 1000 + Math.floor(ns / 1000000))
  } else if (typeof dateVal === 'string' || typeof dateVal === 'number') {
    d = new Date(dateVal)
  } else {
    return ''
  }

  if (isNaN(d.getTime())) {
    return typeof dateVal === 'string' ? dateVal : ''
  }

  return new Intl.DateTimeFormat(locale || undefined, {
    timeZone: timeZone || undefined,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d)
}

/**
 * Formats an unknown timestamp value (ISO string, Timestamp, Date, or millis) into a short localized date/time string.
 * Used by FamilyClassTab and MessagesTab.
 */
export function formatEduDateTime(timestamp: unknown, locale?: string, timeZone?: string): string {
  if (!timestamp) return ''
  let d: Date
  if (timestamp instanceof Date) {
    d = timestamp
  } else if (
    typeof timestamp === 'object' &&
    timestamp !== null &&
    'toDate' in timestamp &&
    typeof (timestamp as { toDate: () => unknown }).toDate === 'function'
  ) {
    d = (timestamp as { toDate: () => Date }).toDate()
  } else if (
    typeof timestamp === 'object' &&
    timestamp !== null &&
    typeof (timestamp as { _seconds?: unknown })._seconds === 'number'
  ) {
    const s = (timestamp as { _seconds: number; _nanoseconds?: number })._seconds
    const ns = (timestamp as { _seconds: number; _nanoseconds?: number })._nanoseconds || 0
    d = new Date(s * 1000 + Math.floor(ns / 1000000))
  } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    d = new Date(timestamp)
  } else {
    return ''
  }

  if (isNaN(d.getTime())) {
    return typeof timestamp === 'string' ? timestamp : ''
  }

  const now = new Date()
  let isSameDay: boolean
  if (timeZone) {
    try {
      const dStr = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(d)
      const nowStr = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(now)
      isSameDay = dStr === nowStr
    } catch {
      isSameDay = false
    }
  } else {
    isSameDay =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate()
  }

  if (isSameDay) {
    return new Intl.DateTimeFormat(locale || undefined, {
      timeZone: timeZone || undefined,
      hour: 'numeric',
      minute: '2-digit',
    }).format(d)
  }

  return new Intl.DateTimeFormat(locale || undefined, {
    timeZone: timeZone || undefined,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d)
}

